import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'
import {
  Editor,
  EditorState,
  convertToRaw,
  convertFromRaw,
  RichUtils,
  getDefaultKeyBinding,
  KeyBindingUtil,
} from 'draft-js'
import { mdToDraftjs, draftjsToMd } from 'draftjs-md-converter'

import textFieldShape from '../text-field/text-field-shape'
import getStyles from '../text-field/styles'
import { addMention } from './utils'
import { withTheme, IWithThemeProps } from '../../theme'

type RichTextFieldVariant = 'normal' | 'success'

const variants: RichTextFieldVariant[] = ['normal', 'success']

const mdDict = { BOLD: '**' }

const fromEditor = (editorState: EditorState) => draftjsToMd(convertToRaw(editorState.getCurrentContent()), mdDict)
const toEditor = (value = '') => convertFromRaw(mdToDraftjs(value))
const setEditorState = (value?: string) => EditorState.createWithContent(toEditor(value))

function keyBindingFn(e: React.KeyboardEvent) {
  if (e.keyCode === 13 /* `enter` key */ && KeyBindingUtil.hasCommandModifier(e)) {
    return 'dooer-submit'
  }

  return getDefaultKeyBinding(e)
}

interface IRichTextFieldProps {
  initialValue?: string
  onChange?: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  onSubmit?: () => void
  innerRef?: (ref: any) => void

  style?: Rule
  wrapperStyle?: Rule
  placeholder?: string
  flush?: boolean
  readOnly?: boolean
  hasErrored?: boolean
  variant?: RichTextFieldVariant
  disabled?: boolean
  height?: string
}

export const RichTextField = withTheme(
  class RichTextField extends React.Component<IRichTextFieldProps & IWithThemeProps> {
    static propTypes = {
      initialValue: PropTypes.string,
      placeholder: PropTypes.string,
      onChange: PropTypes.func,
      onSubmit: PropTypes.func,
      onFocus: PropTypes.func,
      onBlur: PropTypes.func,
      wrapperStyle: PropTypes.object,
      flush: PropTypes.bool,
      disabled: PropTypes.bool,
      variant: PropTypes.oneOf(variants),
      height: PropTypes.string,
      ...textFieldShape,
    } as any

    static defaultProps = {
      onChange: () => {
        // empty onChange as default
      },
      variant: 'normal' as RichTextFieldVariant,
      height: '80px',
    }

    constructor(props: IRichTextFieldProps & IWithThemeProps) {
      super(props)
      this.editorRef = React.createRef()
    }

    state = {
      editorState: setEditorState(this.props.initialValue || ''),
      focused: false,
    }

    onChange = (editorState: EditorState) => {
      this.props.onChange && this.props.onChange(fromEditor(editorState))
      this.setState({ editorState })
    }

    onFocus = () => {
      this.props.onFocus && this.props.onFocus()
      this.setState({ focused: true })
    }
    onBlur = () => {
      this.props.onBlur && this.props.onBlur()
      this.setState({ focused: false })
    }

    handleKeyCommand = (command: string) => {
      if (this.props.onSubmit && command === 'dooer-submit') {
        this.props.onSubmit()
        return 'handled'
      }

      const newState = RichUtils.handleKeyCommand(this.state.editorState, command)
      if (newState) {
        this.onChange(newState)
        return 'handled'
      }
      return 'not-handled'
    }

    focus = () => {
      if (this.editorRef.current) {
        this.editorRef.current.focus()
      }
      this.onFocus()
    }

    blur = () => {
      if (this.editorRef.current) {
        this.editorRef.current.blur()
      }
      this.onBlur()
    }

    clear = () => this.setState({ editorState: setEditorState() })

    set = (value: string) => this.setState({ editorState: setEditorState(value) })

    addMention = ({ name, url }: { name: string; url: string }) => {
      const { editorState } = this.state
      const newEditorState = addMention(editorState, { name, url })
      this.onChange(newEditorState)
    }

    editorRef: React.RefObject<HTMLInputElement>

    render() {
      const { style, placeholder, flush, readOnly, hasErrored, variant, disabled, height, wrapperStyle } = this.props
      const { editorState, focused } = this.state
      const contentState = editorState.getCurrentContent()

      const styles = getStyles(this.props.theme)

      const inputStyles = [
        styles.input.base,
        styles.richtext.base,
        !readOnly && !hasErrored && variant === 'normal' && styles.input.normal,
        variant === 'success' && !readOnly && styles.input.success,
        hasErrored && styles.input.error,
        readOnly && styles.input.readOnly,
        disabled && styles.input.disabled,
        focused && styles.input.normal[':focus'],
        window.navigator.userAgent &&
          window.navigator.userAgent.includes('iPhone') && {
            fontSize: '16px',
          },
        height && { minHeight: height },
        style,
      ]

      const wrapperStyles = [
        styles.wrapper.base,
        styles.richtext.base,
        flush && styles.wrapper.flush,
        height && { minHeight: height },
        {
          '& .public-DraftEditorPlaceholder-root': {
            position: 'absolute',
          },
        },
      ]

      return (
        // only focus is needed for keyboard navigation, after the editor is focused it handles all keyboard interactions
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div onClick={this.focus} {...css(wrapperStyles, wrapperStyle)}>
          <div {...css(inputStyles)}>
            <Editor
              editorState={editorState}
              handleKeyCommand={this.handleKeyCommand}
              keyBindingFn={keyBindingFn}
              onBlur={this.onBlur}
              onChange={this.onChange}
              onFocus={this.onFocus}
              placeholder={contentState.hasText() ? undefined : placeholder}
              readOnly={disabled || readOnly}
              ref={ref => {
                if (this.props.innerRef) {
                  this.props.innerRef(this)
                }

                ;(this.editorRef.current as any) = ref
              }}
            />
          </div>
        </div>
      )
    }
  }
)
